import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoicesAPI, designerAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getStatusColor, generateWhatsAppLink, generateInvoiceMessage } from '../utils/helpers';
import { Plus, WhatsappLogo, Lock, Crown, Gift } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const FREE_INVOICES_LIMIT = 10;

export const InvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    fetchData();
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(response.data.is_active);
      localStorage.setItem('is_premium', response.data.is_active ? 'true' : 'false');
    } catch (error) {
      setIsPremium(localStorage.getItem('is_premium') === 'true');
    }
  };

  const fetchData = async () => {
    try {
      const [invoicesRes, profileRes] = await Promise.all([
        invoicesAPI.getAll(),
        designerAPI.getProfile(),
      ]);
      setInvoices(invoicesRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const shareViaWhatsApp = (invoice) => {
    const message = generateInvoiceMessage(invoice, profile.name, profile.upi_id);
    window.open(generateWhatsAppLink(invoice.customer_mobile, message), '_blank');
  };

  // Determine which invoices to show (first 10 are free)
  const visibleInvoices = isPremium ? invoices : invoices.slice(0, FREE_INVOICES_LIMIT);
  const lockedInvoicesCount = isPremium ? 0 : Math.max(0, invoices.length - FREE_INVOICES_LIMIT);
  const canCreateMore = isPremium || invoices.length < FREE_INVOICES_LIMIT;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="invoices-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
              Invoices
            </h1>
            <p className="text-[#6B7280] font-medium leading-relaxed">
              {invoices.length} total invoices
              {!isPremium && <span className="text-orange-600 ml-2">({FREE_INVOICES_LIMIT} FREE)</span>}
            </p>
          </div>
          <Link to={canCreateMore ? "/invoices/create" : "#"}>
            <Button 
              data-testid="create-invoice-button" 
              className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full"
              disabled={!canCreateMore}
              onClick={(e) => {
                if (!canCreateMore) {
                  e.preventDefault();
                  toast.error(`Free plan मध्ये फक्त ${FREE_INVOICES_LIMIT} invoices बनवता येतात. Premium घ्या!`);
                }
              }}
            >
              <Plus className="h-4 w-4 mr-2" weight="bold" /> Create Invoice
            </Button>
          </Link>
        </div>

        {/* Free Limit Banner */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Gift className="h-5 w-5 text-orange-600" weight="duotone" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Free Plan: {invoices.length}/{FREE_INVOICES_LIMIT} Invoices Used</p>
                  <p className="text-sm text-gray-600">Unlimited invoices साठी Premium घ्या</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/premium')}
                className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full text-sm"
              >
                <Crown className="h-4 w-4 mr-1" weight="fill" /> Upgrade
              </Button>
            </div>
          </div>
        )}

        {/* Invoices List */}
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <Card className="border-[#E5E7EB] bg-white">
              <CardContent className="pt-6">
                <p className="text-center text-[#6B7280]">No invoices yet. Create your first invoice!</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {visibleInvoices.map((invoice, index) => (
                <Card key={invoice.id} data-testid={`invoice-item-${invoice.id}`} className="border-[#E5E7EB] bg-white hover:border-[#FF5A36] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-['Chivo'] text-lg font-bold text-[#121212]">{invoice.invoice_number}</h3>
                              {!isPremium && index < FREE_INVOICES_LIMIT && (
                                <Badge className="bg-green-100 text-green-700 border-0 text-xs">FREE</Badge>
                              )}
                            </div>
                            <p className="text-sm text-[#6B7280]">{invoice.customer_name} • {invoice.customer_mobile}</p>
                            <p className="text-xs text-[#6B7280] mt-1">{formatDate(invoice.created_at)}</p>
                          </div>
                          <Badge className={getStatusColor(invoice.payment_status)} data-testid={`invoice-status-${invoice.id}`}>
                            {invoice.payment_status}
                          </Badge>
                        </div>
                        <div className="bg-[#FAFAFA] p-4 rounded-lg mb-3">
                          <div className="space-y-2">
                            {invoice.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-[#121212]">{item.service_name} x {item.quantity}</span>
                                <span className="font-medium text-[#121212]">{formatCurrency(item.total)}</span>
                              </div>
                            ))}
                            <div className="border-t border-[#E5E7EB] pt-2 mt-2">
                              <div className="flex justify-between text-sm font-medium">
                                <span className="text-[#121212]">Total</span>
                                <span className="text-[#FF5A36] text-lg">{formatCurrency(invoice.total)}</span>
                              </div>
                              {invoice.advance_paid > 0 && (
                                <>
                                  <div className="flex justify-between text-sm mt-1">
                                    <span className="text-[#6B7280]">Advance Paid</span>
                                    <span className="text-green-600">{formatCurrency(invoice.advance_paid)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm font-bold mt-1">
                                    <span className="text-[#121212]">Balance</span>
                                    <span className="text-red-600">{formatCurrency(invoice.balance)}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {invoice.notes && (
                          <p className="text-sm text-[#6B7280] italic">Note: {invoice.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          data-testid={`share-invoice-${invoice.id}`}
                          onClick={() => shareViaWhatsApp(invoice)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <WhatsappLogo className="h-4 w-4 mr-1" weight="fill" /> Share
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Locked Invoices Indicator */}
              {lockedInvoicesCount > 0 && (
                <Card className="border-2 border-dashed border-orange-300 bg-orange-50/50">
                  <CardContent className="py-8 text-center">
                    <Lock className="h-12 w-12 text-orange-400 mx-auto mb-3" weight="duotone" />
                    <h3 className="font-bold text-lg text-gray-900 mb-1">
                      +{lockedInvoicesCount} More Invoices Locked 🔒
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Premium Plan घ्या आणि सर्व invoices access करा
                    </p>
                    <Button 
                      onClick={() => navigate('/premium')}
                      className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full"
                    >
                      <Crown className="h-4 w-4 mr-2" weight="fill" /> Unlock All Invoices
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
